import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import App from "./App";
import { Provider } from "react-redux";
import { ConfigProvider, Spin, App as AntdAppContextHolder } from 'antd';
import AppContextProvider from "./AppContext";
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import fr_FR from 'antd/es/locale/fr_FR';
import store from "./store";
import { AuthProvider } from "react-oidc-context";
import { ReactComponent as Logo } from './media/stetho.svg';
import { GlobalConfigProvider } from "./GlobalConfigContext";

dayjs.locale('fr');

// "ResizeObserver loop completed with undelivered notifications" (et l'ancienne variante
// "limit exceeded") est une erreur bénigne émise par divers usages de ResizeObserver
// (Ant Design, composants internes…). Elle n'a aucun impact runtime mais déclenche l'overlay
// d'erreur du dev-server. On la neutralise uniquement côté affichage.
const RESIZE_OBSERVER_ERROR = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
const swallowResizeObserverError = (message) => {
  if (typeof message === 'string' && RESIZE_OBSERVER_ERROR.test(message)) {
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    if (overlay) overlay.style.display = 'none';
    return true;
  }
  return false;
};
window.addEventListener('error', (e) => {
  if (swallowResizeObserverError(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});
window.addEventListener('unhandledrejection', (e) => {
  if (swallowResizeObserverError(e.reason && e.reason.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

const DEFAULT_KC_FRONT_URL = "http://localhost:8080/realms/KEYCLOAK-RAVAIL";

const buildOidcConfig = (authority) => ({
  authority: authority,
  client_id: "ravail_app",
  redirect_uri: window.location.origin + "/login",
  post_logout_redirect_uri: window.location.origin + "/logout",
  // Page dédiée obligatoire : sans elle, silent_redirect_uri retombe sur redirect_uri
  // ("/login"), l'iframe cachée charge donc toute la SPA, ne notifie jamais la fenêtre
  // parente, et chaque signinSilent échoue au bout de 10 s ("IFrame timed out without
  // a response"). Cf. frontend/public/silent-renew.html.
  silent_redirect_uri: window.location.origin + "/silent-renew.html",
  response_type: "code",
  scope: "openid profile email",
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTime: 60,
  // monitorSession utilise une iframe « check session » vers Keycloak, bloquée
  // par l'ITP de WebKit (iOS/iPad) → fausses détections de fin de session.
  // Le renew passe par le refresh_token (POST direct), qui fonctionne sur iOS.
  monitorSession: false,
});

function AppWrapper() {
  const SpinnerLogo = <Logo id="beatingLogo" style={{ fontSize: 94 }}/>;

  const [configLoaded, setConfigLoaded] = useState(false);
  const [oidcConfig, setOidcConfig] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);

  useEffect(() => {
    fetch('/config.json')
      .then(response => response.json())
      .then(data => {
        setGlobalConfig(data);
        const authority = data.KC_FRONT_URL && data.KC_FRONT_URL.includes("http") ? data.KC_FRONT_URL : DEFAULT_KC_FRONT_URL;
        setOidcConfig(buildOidcConfig(authority));
        setConfigLoaded(true);
      })
      .catch(error => {
        // Sans ce catch, un /config.json injoignable ou invalide laisse l'application
        // bloquée indéfiniment sur le spinner. On démarre sur la configuration par défaut.
        console.error("Chargement de /config.json impossible", error);
        setGlobalConfig({});
        setOidcConfig(buildOidcConfig(DEFAULT_KC_FRONT_URL));
        setConfigLoaded(true);
      });
  }, []);

  if (!configLoaded) {
    // Affiche un écran de chargement pendant la récupération de la configuration
    return <div className='conteneur-flex'><Spin spinning={!configLoaded} size="large" indicator={SpinnerLogo}></Spin></div>
  }

  return (
    <Provider store={store}>
        <ConfigProvider
          locale={fr_FR}
          theme={{
            token: {
              colorPrimary: '#04aceb',
              colorLink: "#04aceb",
              colorInfo: "#04aceb",
              colorSuccess: "#76e297",
              colorError: "#f97575",
              colorWarning: "#ffa622",
              colorTextBase: "#233551",
              colorBgSpotlight: "rgb(46 59 92 / 100%)",
              fontSize: 16
            },
          }}
        >
          <BrowserRouter>
            <AntdAppContextHolder>
              <GlobalConfigProvider config={globalConfig}>
                <AppContextProvider>
                  <AuthProvider {...oidcConfig}>
                    <App />
                  </AuthProvider>
                </AppContextProvider>
              </GlobalConfigProvider>
            </AntdAppContextHolder>
          </BrowserRouter>
        </ConfigProvider>
    </Provider>
  );
}

ReactDOM.render(
  <AppWrapper />,
  document.getElementById('root')
);
